import _ from 'lodash';
import {
  IntegrationAppProvider,
  useConnection,
  useIntegrations,
} from '@membranehq/react';
import { useEffect, useMemo, useState } from 'react';
import Accordion from 'src/components/Accordion';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import Table, { Header } from 'src/components/Table';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { StabilityLevel } from 'src/types/stability-level';
import { useParams } from 'react-router-dom';
import { Input } from 'src/components/Input';
import { MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { IconButton } from 'src/components/IconButton';

const GET_AVAILABLE_OBJECTS_ACTION_KEY = 'view-available-objects';

type CrmField = {
  title: string;
  fieldName: string;
  type: string;
  referenceRecords: string;
};

const headers: Header[] = [
  { key: 'title', label: 'Title', disableSorting: true },
  { key: 'fieldName', label: 'Field Name', disableSorting: true },
  { key: 'referenceRecords', label: 'Reference Records', disableSorting: true },
  { key: 'type', label: 'Type', disableSorting: true },
];

// No nested fields. Only one level deep
type FieldMapping = {
  variableName: string;
  entityName: string;
  fieldName: string;
  type: string;
};

const fieldMappingHeaders: Header[] = [
  { key: 'variableName', label: 'Variable Name', disableSorting: true },
  { key: 'entityName', label: 'Entity Name', disableSorting: true },
  { key: 'fieldName', label: 'Field Name', disableSorting: true },
  { key: 'actions', label: '', disableSorting: true },
];

const IntegrationDataMappingContent = () => {
  const { integrationId } = useParams();
  const { integrations } = useIntegrations();
  const [collections, setCollections] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [selectedFields, setSelectedFields] = useState<
    Record<string, CrmField[]>
  >({});
  const [globalVariableMappings, setGlobalVariableMappings] = useState<
    FieldMapping[]
  >([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(
    globalVariableMappings,
  );
  const { accessor: connectionAccessor } = useConnection(integrationId);

  const integration = useMemo(
    () => integrations.find((integration) => integration.id === integrationId),
    [integrations, integrationId],
  );

  useEffect(() => {
    // TODO: Load previously saved global variable mappings and initialize field mappings
    setGlobalVariableMappings([]);
    setFieldMappings([]);
  }, []);

  useEffect(() => {
    if (!integrationId || !integration?.connection?.id) return;

    const getAppSchema = async () => {
      const result = await connectionAccessor
        ?.action(GET_AVAILABLE_OBJECTS_ACTION_KEY)
        .run();
      setCollections(result?.output?.objects || []);
    };
    getAppSchema();
  }, [integrationId, integration?.connection?.id]);

  const formatDefaultVariableName = (
    collectionKey: string,
    fieldName: string,
  ) => {
    return `${integration!.name}.${collectionKey}.${fieldName}`
      .replace(/\s/g, '')
      .toLowerCase();
  };

  const handleAddGlobalVariable = () => {
    const newFieldMappings = [...fieldMappings];
    Object.entries(selectedFields).forEach(([collectionKey, rows]) => {
      rows.forEach((row) => {
        newFieldMappings.push({
          variableName: formatDefaultVariableName(collectionKey, row.title),
          entityName: collectionKey,
          fieldName: row.title,
          type: row.type,
        });
      });
    });
    setSelectedFields({}); // After adding, clear the selected fields
    setFieldMappings(newFieldMappings);
  };

  const handleSaveGlobalVariableMappings = () => {
    // TODO: Write to durable storage and update the global variable mappings
    setGlobalVariableMappings(fieldMappings);
  };

  return (
    <div className="flex-1">
      <Heading
        title="Data Mapping"
        subtitle="Map data from your CRM to Fluents properties"
        stabilityLevel={StabilityLevel.Experimental}
      />
      <ContentWrapper>
        <div className="flex gap-4">
          {/* Left-hand side */}
          <div className="flex flex-col gap-4 w-1/3">
            <div className="flex justify-between items-center">
              <h2>CRM Fields</h2>
              <Button
                color="primary"
                size="small"
                className="w-48"
                onClick={() => handleAddGlobalVariable()}
                disabled={Object.values(selectedFields).flat().length === 0} // Disable if no fields are selected
              >
                Add Global Variable
              </Button>
            </div>

            <Input
              placeholder="Search"
              className="w-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<MagnifyingGlass size={20} />}
            />
            <div className="flex flex-col gap-4">
              {collections.map((collection) => {
                const properties = collection?.fieldsSchema?.properties || {};
                const rows = Object.keys(properties)
                  .map((key) => {
                    const property = properties[key];
                    if (
                      search &&
                      !key.toLowerCase().includes(search.toLowerCase()) &&
                      !property.title
                        ?.toLowerCase()
                        .includes(search.toLowerCase()) &&
                      !selectedFields[collection.key]?.some(
                        (field) => field.fieldName === key,
                      ) // Preserve selected fields regardless of search
                    )
                      return null;
                    if (
                      fieldMappings.some(
                        (fieldMapping) =>
                          fieldMapping.entityName === collection.key &&
                          fieldMapping.fieldName === property.title,
                      )
                    ) {
                      return null;
                    }
                    let referenceRecords = '';
                    let baseEntity = property.items ?? property;
                    if (baseEntity.referenceRecords) {
                      referenceRecords = JSON.stringify(
                        property.referenceRecords,
                      );
                    } else if (baseEntity.referenceCollection) {
                      referenceRecords = JSON.stringify(
                        baseEntity.referenceCollection.key,
                      );
                    }
                    return {
                      fieldName: key,
                      title: property.title,
                      type: property.type,
                      referenceRecords,
                    };
                  })
                  .filter((row) => row !== null);

                if (rows.length === 0) return null;

                return (
                  <Accordion
                    key={collection.key}
                    title={collection.name}
                    defaultOpen={true}
                  >
                    <Table
                      headers={headers}
                      rows={rows}
                      totalItems={rows.length}
                      currentPage={1}
                      onPageChange={() => {}}
                      onSort={() => {}}
                      loading={false}
                      disablePagination={true}
                      enableRowSelection={true}
                      selectedRows={rows
                        .map((row, index) => {
                          const isSelected = selectedFields[
                            collection.key
                          ]?.some((field) => field.fieldName === row.fieldName);
                          return isSelected ? index : -1;
                        })
                        .filter((index) => index !== -1)}
                      onRowSelectionChange={(rowSelections) => {
                        setSelectedFields({
                          ...selectedFields,
                          [collection.key]: rows.filter((_, index) =>
                            rowSelections.includes(index),
                          ),
                        });
                      }}
                    />
                  </Accordion>
                );
              })}
            </div>
          </div>
          {/* Right-hand side */}
          <div className="flex flex-col gap-4 w-2/3">
            <div className="flex justify-between items-center">
              <h2>Global Variables</h2>
              <Button
                color="primary"
                size="small"
                className="w-48"
                onClick={handleSaveGlobalVariableMappings}
                disabled={_.isEqual(fieldMappings, globalVariableMappings)} // Disable if no changes
              >
                Save
              </Button>
            </div>
            <Table
              headers={fieldMappingHeaders}
              rows={fieldMappings.map((mapping) => ({
                variableName: (
                  <Input
                    value={mapping.variableName}
                    onChange={(e) =>
                      setFieldMappings(
                        fieldMappings.map((fieldMapping) =>
                          fieldMapping.variableName === mapping.variableName
                            ? { ...fieldMapping, variableName: e.target.value }
                            : fieldMapping,
                        ),
                      )
                    }
                  />
                ),
                entityName: mapping.entityName,
                fieldName: mapping.fieldName,
                type: mapping.type,
                actions: (
                  <IconButton
                    aria-label={`Remove ${mapping.variableName}`}
                    className="text-error"
                    onClick={() => {
                      // Filter creates a new array and does not mutate the original array
                      setFieldMappings(
                        fieldMappings.filter(
                          (fieldMapping) =>
                            mapping.variableName !== fieldMapping.variableName,
                        ),
                      );
                    }}
                  >
                    <Trash size={20} />
                  </IconButton>
                ),
              }))}
              totalItems={fieldMappings.length}
              defaultSize={30}
              currentPage={1}
              onPageChange={() => {}}
              onSort={() => {}}
              loading={false}
              disablePagination={true}
            />
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
};

export const IntegrationDataMapping = () => {
  const { get } = useAuthedRequest();
  const [token, setToken] = useState('');

  const getIntegrationAppToken = async () => {
    const response = await get('/account-connections/integration-app/token');

    if (response.token) setToken(response.token);
  };

  useEffect(() => {
    getIntegrationAppToken();
  }, []);

  return (
    <IntegrationAppProvider token={token}>
      <IntegrationDataMappingContent />
    </IntegrationAppProvider>
  );
};
